import { callBackend, callBackendBlob } from "./api";
import { parseCloudRunError } from "../components/msiDriftReviewHelpers";
import { isNetworkFailure, NETWORK_FAILURE_MESSAGE } from "./networkFailure";

// A lost connection reads as one plain message, not the browser's TypeError.
const failureMessage = (err) => (isNetworkFailure(err) ? NETWORK_FAILURE_MESSAGE : String(err));

export async function fetchCurrentUserData() {
    const response = await callBackend('fetch_user_data', 'POST', {});
    if (!response?.userData || typeof response.userData !== 'object') {
        throw new Error('Current user preferences response is invalid.');
    }
    return response.userData;
}

export async function stageWatchlistPortfolioEditor(payload) {
    return callBackend('api/portfolio/imports/watchlist-editor/stage', 'POST', payload);
}

export async function deleteSecurityResolutionGroups({
    deletions,
    attemptId,
} = {}) {
    return callBackend('users/security-resolution/groups/delete', 'POST', {
        deletions: (Array.isArray(deletions) ? deletions : []).map((deletion) => ({
            delete_token: deletion?.deleteToken,
            confirmed_portfolio_removal: Boolean(
                deletion?.confirmedPortfolioRemoval,
            ),
        })),
        attempt_id: attemptId,
    });
}

export async function stageSecurityResolutionRemovals({ deletions, attemptId } = {}) {
    return callBackend('users/security-resolution/groups/stage-removal', 'POST', {
        deletions: (Array.isArray(deletions) ? deletions : []).map((deletion) => ({
            delete_token: deletion?.deleteToken,
            confirmed_portfolio_removal: Boolean(deletion?.confirmedPortfolioRemoval),
        })),
        attempt_id: attemptId,
    });
}

function watchlistRevisionErrorResponse(error) {
    const parsed = parseCloudRunError(error);
    return {
        success: false,
        message: parsed.message,
        code: parsed.code,
        status_code: parsed.statusCode,
        detail: parsed.detail,
    };
}

function watchlistRevisionThrownError(error) {
    const parsed = parseCloudRunError(error);
    const structuredError = new Error(parsed.message);
    structuredError.code = parsed.code;
    structuredError.statusCode = parsed.statusCode;
    structuredError.status_code = parsed.statusCode;
    structuredError.detail = parsed.detail;
    return structuredError;
}

export async function processWatchlistFile(params) {
    try {
        const res = await callBackend('process_watchlist_file', 'POST', params);
        return res;
    } catch (err) {
        const parsed = parseCloudRunError(err);
        return {
            success: false,
            message: parsed.message,
            code: parsed.code,
            status_code: parsed.statusCode,
            detail: parsed.detail,
        };
    }
}

// The server's column check for a Watchlist file; nothing is staged.
export async function reviewWatchlistFileColumns(matrix) {
    try {
        return await callBackend('review_watchlist_file_columns', 'POST', { matrix });
    } catch (err) {
        return { success: false, message: parseCloudRunError(err).message };
    }
}

export async function updateMissingFactorPreferences({
    policy,
    axiomaModel,
    barraModel,
} = {}) {
    const payload = {};
    if (policy !== undefined) {
        payload.policy = policy;
    }
    if (axiomaModel !== undefined) {
        payload.axioma_model = axiomaModel;
    }
    if (barraModel !== undefined) {
        payload.barra_model = barraModel;
    }
    try {
        const res = await callBackend('users/missing-factor-policy', 'POST', payload);
        return res;
    } catch (err) {
        const parsed = parseCloudRunError(err);
        return {
            success: false,
            message: parsed.message,
            code: parsed.code,
            status_code: parsed.statusCode,
            detail: parsed.detail,
        };
    }
}

export async function updateMissingFactorPolicy(policy) {
    return updateMissingFactorPreferences({ policy });
}

export async function updateBetaSource(betaSource) {
    try {
        const res = await callBackend('users/beta-source', 'POST', {
            beta_source: betaSource,
        });
        return res;
    } catch (err) {
        return {
            success: false,
            message: failureMessage(err),
        };
    }
}

export async function updateRiskContext({
    factorModel,
    idioVolSource,
} = {}) {
    const payload = {};
    if (factorModel !== undefined) {
        payload.factor_model = factorModel;
    }
    if (idioVolSource !== undefined) {
        payload.idio_vol_source = idioVolSource;
    }

    try {
        const res = await callBackend('users/risk-context', 'POST', payload);
        return res;
    } catch (err) {
        const parsed = parseCloudRunError(err);
        return {
            success: false,
            message: parsed.message,
            code: parsed.code,
            status_code: parsed.statusCode,
            detail: parsed.detail,
        };
    }
}

export async function fetchFactorLoaderRunHistory({
    riskSource,
    factorDataModel,
    status,
    limit = 50,
} = {}) {
    const params = new URLSearchParams();
    if (riskSource !== undefined && riskSource !== null && String(riskSource).trim() !== '') {
        params.set('risk_source', String(riskSource).trim());
    }
    if (factorDataModel !== undefined && factorDataModel !== null && String(factorDataModel).trim() !== '') {
        params.set('factor_data_model', String(factorDataModel).trim());
    }
    if (status !== undefined && status !== null && String(status).trim() !== '') {
        params.set('status', String(status).trim());
    }
    if (limit !== undefined && limit !== null) {
        params.set('limit', String(limit));
    }

    const query = params.toString();
    const endpoint = query ? `admin/factor-data/run-history?${query}` : 'admin/factor-data/run-history';

    try {
        return await callBackend(endpoint, 'GET');
    } catch (err) {
        return {
            success: false,
            message: failureMessage(err),
            items: [],
        };
    }
}

export async function fetchAdminFactorDataInventory({
    loader = 'all',
    days = 90,
} = {}) {
    const params = new URLSearchParams();
    if (loader !== undefined && loader !== null && String(loader).trim() !== '') {
        params.set('loader', String(loader).trim());
    }
    if (days !== undefined && days !== null) {
        params.set('days', String(days));
    }

    const query = params.toString();
    const endpoint = query ? `admin/factor-data/inventory?${query}` : 'admin/factor-data/inventory';

    try {
        return await callBackend(endpoint, 'GET');
    } catch (err) {
        return {
            success: false,
            message: failureMessage(err),
            items: [],
        };
    }
}

export async function fetchAiIdioModelMetadata({
    factorModel,
    factorDataModel,
    factorSnapshotDate,
    livePerformanceLimit,
    performanceScope,
} = {}) {
    const payload = {};
    if (factorModel !== undefined) {
        payload.factor_model = factorModel;
    }
    if (factorDataModel !== undefined) {
        payload.factor_data_model = factorDataModel;
    }
    if (factorSnapshotDate !== undefined) {
        payload.factor_snapshot_date = factorSnapshotDate;
    }
    if (livePerformanceLimit !== undefined) {
        payload.live_performance_limit = livePerformanceLimit;
    }
    if (performanceScope !== undefined && performanceScope !== null) {
        payload.performance_scope = performanceScope;
    }

    try {
        return await callBackend('users/ai-idio-model-metadata', 'POST', payload);
    } catch (err) {
        return {
            success: false,
            message: failureMessage(err),
            metadata: null,
        };
    }
}

export async function fetchMsiDriftStatus() {
    try {
        return await callBackend('users/msi-drift-status', 'GET');
    } catch (err) {
        return {
            success: false,
            message: failureMessage(err),
            has_drift: false,
        };
    }
}

export async function fetchMsiHealthSummary() {
    try {
        return await callBackend('users/msi-health-summary', 'GET');
    } catch (err) {
        return {
            success: false,
            message: failureMessage(err),
            active_snapshot_version: null,
            last_reviewed_snapshot_version: null,
            has_drift: false,
            needs_action_count: 0,
            changed_key_count_total: 0,
            auto_resolved_count_lifetime: 0,
            auto_resolved_count_30d: 0,
            last_auto_resolved_at: null,
            status_level: 'healthy',
        };
    }
}

export async function fetchSecurityResolutionCenter() {
    try {
        return await callBackend('users/security-resolution-center', 'GET');
    } catch (err) {
        return {
            success: false,
            message: failureMessage(err),
            status: 'degraded',
            active_snapshot_version: null,
            last_reviewed_snapshot_version: null,
            portfolio_analysis_admission: {
                factor_data_available: false,
                readiness_is_final: false,
            },
            summary: {
                row_count: 0,
                listing_count: 0,
                group_count: 0,
                selected_count: 0,
                available_count: 0,
                input_issue_count: 0,
                watchlist_ready_count: 0,
                portfolio_analysis_ready_count: 0,
                needs_attention_count: 0,
                pending_count: 0,
                has_pending: false,
                poll_recommended: false,
                poll_after_ms: null,
            },
            groups: [],
            rows: [],
            reconciliation_review: {
                rows: [],
                row_count: 0,
                actionable_count: 0,
            },
        };
    }
}

export async function acknowledgeMsiReconciliationRemovedRow({ rowId } = {}) {
    try {
        return await callBackend('users/msi-reconciliation/removed-row/acknowledge', 'POST', {
            row_id: rowId,
        });
    } catch (err) {
        return {
            success: false,
            message: failureMessage(err),
        };
    }
}

export async function acceptMsiReconciliationSuccessor({ rowId, candidateId } = {}) {
    try {
        return await callBackend('users/msi-reconciliation/removed-row/accept-successor', 'POST', {
            row_id: rowId,
            candidate_id: candidateId,
        });
    } catch (err) {
        return {
            success: false,
            message: failureMessage(err),
        };
    }
}

export async function fetchWatchlistResolutionSummary() {
    try {
        return await callBackend('users/watchlist-resolution-summary', 'GET');
    } catch (err) {
        return {
            success: false,
            message: failureMessage(err),
            visible_count: 0,
            waiting_count: 0,
            unresolved_count: 0,
            excluded_upload_count: 0,
            industry_unresolved_count: 0,
            total_industry_count: 0,
        };
    }
}

export async function fetchLatestWatchlistRevisions() {
    try {
        return await callBackend('api/watchlist/revisions/latest', 'GET');
    } catch (err) {
        return {
            success: false,
            message: failureMessage(err),
            current_revision: null,
            user_watchlist_state: null,
            recent_revisions: [],
            pending_revision: null,
        };
    }
}

export async function fetchCurrentWatchlistRevisionSignal() {
    return callBackend('api/watchlist/revisions/current-signal', 'GET');
}

export async function fetchWatchlistContextStatus() {
    return callBackend('api/watchlist/revisions/context-status', 'GET');
}

export async function fetchFactorDatasets({ beforeDate, limit = 100 } = {}) {
    const params = new URLSearchParams({ limit: String(limit) });
    if (beforeDate) params.set('before_date', beforeDate);
    return callBackend(`api/watchlist/revisions/factor-datasets?${params}`, 'GET');
}

export async function stageFactorDatasetSelection({ attemptId, expectedRevisionId, selectionMode, releaseId }) {
    return callBackend('api/watchlist/revisions/factor-datasets/stage', 'POST', {
        attempt_id: attemptId,
        expected_watchlist_revision_id: expectedRevisionId,
        selection_mode: selectionMode,
        ...(selectionMode === 'pinned' ? { factor_release_id: releaseId } : {}),
    });
}

export async function fetchFactorDataSyncPreferences() {
    return callBackend('users/factor-data-sync-preferences', 'GET');
}

export async function saveFactorDataSyncPreferences(enabled) {
    return callBackend('users/factor-data-sync-preferences', 'POST', { enabled });
}

export async function fetchPosSizingSyncPreferences() {
    return callBackend('users/pos-sizing-sync-preferences', 'GET');
}

export async function savePosSizingSyncPreferences(enabled) {
    return callBackend('users/pos-sizing-sync-preferences', 'POST', { enabled });
}

export async function fetchPosSizingContextUpdates() {
    return callBackend('pos-sizing/context-updates?limit=100', 'GET');
}

export async function stageWatchlistContextRefresh(attemptId) {
    return callBackend('api/watchlist/revisions/context-refresh/stage', 'POST', {
        attempt_id: attemptId,
    });
}

export async function fetchWatchlistRevisions({
    offset = 0,
    limit = 10,
} = {}) {
    const params = new URLSearchParams();
    params.set('offset', String(offset));
    params.set('limit', String(limit));

    try {
        return await callBackend(`api/watchlist/revisions/?${params.toString()}`, 'GET');
    } catch (err) {
        return {
            success: false,
            message: failureMessage(err),
            items: [],
            offset,
            limit,
            has_more: false,
        };
    }
}

export async function fetchWatchlistRevision(revisionId) {
    const normalizedRevisionId = revisionId == null ? '' : String(revisionId).trim();
    if (!normalizedRevisionId) {
        return {
            success: false,
            message: 'Missing watchlist revision id.',
        };
    }

    try {
        return await callBackend(
            `api/watchlist/revisions/${encodeURIComponent(normalizedRevisionId)}`,
            'GET',
        );
    } catch (err) {
        return {
            success: false,
            message: failureMessage(err),
        };
    }
}

// Read one revision's user-facing version number now, so surfaces that only
// keep a revision id show the same number as Watchlist History.
export async function fetchWatchlistRevisionVersion(revisionId) {
    const normalizedRevisionId = revisionId == null ? '' : String(revisionId).trim();
    if (!normalizedRevisionId) {
        return { success: false, message: 'Missing watchlist revision id.' };
    }
    try {
        return await callBackend(
            `api/watchlist/revisions/${encodeURIComponent(normalizedRevisionId)}/version`,
            'GET',
        );
    } catch (err) {
        return { success: false, message: failureMessage(err) };
    }
}

export async function fetchWatchlistRevisionSnapshot({
    revisionId,
    offset = 0,
    limit = 100,
    rowKind,
    bucket,
} = {}) {
    const normalizedRevisionId = revisionId == null ? '' : String(revisionId).trim();
    if (!normalizedRevisionId) {
        return {
            success: false,
            message: 'Missing watchlist revision id.',
            items: [],
            offset,
            limit,
            total: 0,
            has_more: false,
        };
    }

    const params = new URLSearchParams();
    params.set('offset', String(offset));
    params.set('limit', String(limit));
    if (rowKind) {
        params.set('row_kind', String(rowKind));
    }
    if (bucket) {
        // Server-side bucket filtering is required for correct pagination:
        // client-side filtering of one row_kind page can hide rows that
        // fall onto a later page.
        params.set('bucket', String(bucket));
    }

    try {
        return await callBackend(
            `api/watchlist/revisions/${encodeURIComponent(normalizedRevisionId)}/snapshot?${params.toString()}`,
            'GET',
        );
    } catch (err) {
        return {
            success: false,
            message: failureMessage(err),
            items: [],
            offset,
            limit,
            total: 0,
            has_more: false,
        };
    }
}

function triggerDownload(blob, filename) {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
        return;
    }
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

export async function downloadWatchlistRevisionExport({
    revisionId,
} = {}) {
    const params = new URLSearchParams();
    params.set('format', 'csv');
    const normalizedRevisionId = revisionId == null ? '' : String(revisionId).trim();
    if (normalizedRevisionId) {
        params.set('revision_id', normalizedRevisionId);
    }
    try {
        const result = await callBackendBlob(
            `api/watchlist/revisions/export?${params.toString()}`,
            'GET',
        );
        const fallbackFilename = normalizedRevisionId
            ? `watchlist-history-revision-${normalizedRevisionId}.csv`
            : 'watchlist-history.csv';
        const filename = result?.filename || fallbackFilename;
        if (result?.blob) {
            triggerDownload(result.blob, filename);
        }
        return {
            success: true,
            filename,
        };
    } catch (err) {
        return watchlistRevisionErrorResponse(err);
    }
}

export async function fetchIdioVolComparison({
    factorModel,
    factorDataModel,
    factorSnapshotDate,
    idioVolSource,
} = {}) {
    const payload = {};
    if (factorModel !== undefined) payload.risk_source = factorModel;
    if (factorDataModel !== undefined) payload.factor_data_model = factorDataModel;
    if (factorSnapshotDate !== undefined) payload.factor_snapshot_date = factorSnapshotDate;
    if (idioVolSource !== undefined) payload.idio_vol_source = idioVolSource;

    try {
        return await callBackend('idio_vol_compare', 'POST', payload);
    } catch (err) {
        return {
            success: false,
            message: failureMessage(err),
            rows_truncated: false,
            rows: [],
        };
    }
}

export async function fetchMsiDriftWorklist({
    limit = 100,
    offset = 0,
    includeResolved = false,
} = {}) {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    params.set('include_resolved', includeResolved ? '1' : '0');
    const endpoint = `users/msi-drift-worklist?${params.toString()}`;

    try {
        return await callBackend(endpoint, 'GET');
    } catch (err) {
        return {
            success: false,
            message: failureMessage(err),
            worklist: [],
            changed_key_count_total: 0,
            worklist_count_total: 0,
            limit,
            offset,
            page_truncated: false,
            include_resolved: includeResolved,
        };
    }
}

export async function saveMsiSelectionsBatch({
    selections,
    attemptId,
    expectedWatchlistRevisionId,
    action,
} = {}) {
    try {
        return await callBackend('users/msi-selections/batch', 'POST', {
            selections: Array.isArray(selections) ? selections : [],
            ...(attemptId ? { attempt_id: attemptId } : {}),
            ...(action ? { action } : {}),
            ...(expectedWatchlistRevisionId ? {
                expected_watchlist_revision_id: expectedWatchlistRevisionId,
            } : {}),
        });
    } catch (err) {
        return {
            success: false,
            message: failureMessage(err),
        };
    }
}

export async function previewProviderIdentityReviews({ selections, attemptId, expectedWatchlistRevisionId }) {
    try {
        return await callBackend('users/msi-provider-identity-reviews/preview', 'POST', {
            selections,
            attempt_id: attemptId,
            expected_watchlist_revision_id: expectedWatchlistRevisionId,
        });
    } catch (error) {
        throw watchlistRevisionThrownError(error);
    }
}

export async function applyProviderIdentityReviews({ selections, attemptId, expectedWatchlistRevisionId, previewToken }) {
    try {
        return await callBackend('users/msi-provider-identity-reviews/apply', 'POST', {
            selections,
            attempt_id: attemptId,
            expected_watchlist_revision_id: expectedWatchlistRevisionId,
            preview_token: previewToken,
        });
    } catch (error) {
        throw watchlistRevisionThrownError(error);
    }
}

export async function fetchWatchlistFieldEditor() {
    try {
        return await callBackend('users/watchlist/fields', 'GET');
    } catch (err) {
        return {
            success: false,
            message: failureMessage(err),
            rows: [],
            watchlist_attribute_snapshot: null,
        };
    }
}

export async function applyWatchlistAttributeMutations({
    operations,
    attemptId,
    expectedWatchlistRevisionId,
    expectedAttributeFingerprint,
} = {}) {
    try {
        return await callBackend('users/watchlist/attributes/apply', 'POST', {
            operations: Array.isArray(operations) ? operations : [],
            attempt_id: attemptId,
            expected_watchlist_revision_id: expectedWatchlistRevisionId,
            expected_attribute_fingerprint: expectedAttributeFingerprint,
        });
    } catch (err) {
        return watchlistRevisionErrorResponse(err);
    }
}

export async function previewSecurityResolutionAdd({
    identifierType,
    identifierValue,
    ticker,
    requestedCountry,
} = {}) {
    try {
        return await callBackend('users/security-resolution/add/preview', 'POST', {
            identifier_type: identifierType,
            identifier_value: identifierValue,
            ticker,
            ...(requestedCountry ? { requested_country: requestedCountry } : {}),
        });
    } catch (err) {
        return {
            success: false,
            message: failureMessage(err),
        };
    }
}

export async function searchWatchlistSecurityDiscovery({
    query,
    country,
    limit = 20,
} = {}) {
    try {
        return await callBackend('users/security-resolution/discovery/search', 'POST', {
            contract_version: 'watchlist-security-discovery.v2',
            query,
            country: country || null,
            limit,
        });
    } catch (err) {
        const parsed = parseCloudRunError(err);
        return {
            success: false,
            message: parsed.message,
            code: parsed.code,
            status_code: parsed.statusCode,
            detail: parsed.detail,
        };
    }
}

export async function stageSecurityResolutionAdditions({
    attemptId,
    items,
    expectedWatchlistRevisionId,
} = {}) {
    try {
        return await callBackend('users/security-resolution/add/stage', 'POST', {
            attempt_id: attemptId,
            items: Array.isArray(items) ? items : [],
            ...(expectedWatchlistRevisionId ? {
                expected_watchlist_revision_id: expectedWatchlistRevisionId,
            } : {}),
        });
    } catch (err) {
        return watchlistRevisionErrorResponse(err);
    }
}

export async function stageSecurityResolutionUnresolvedAddition({
    attemptId,
    previewToken,
    requestedCountry,
    expectedWatchlistRevisionId,
} = {}) {
    try {
        return await callBackend('users/security-resolution/add/stage-unresolved', 'POST', {
            attempt_id: attemptId,
            preview_token: previewToken,
            ...(requestedCountry ? { requested_country: requestedCountry } : {}),
            ...(expectedWatchlistRevisionId ? {
                expected_watchlist_revision_id: expectedWatchlistRevisionId,
            } : {}),
        });
    } catch (err) {
        return watchlistRevisionErrorResponse(err);
    }
}

export async function applyWatchlistRevision({
    revisionId,
    attemptId,
    expectedWatchlistRevisionId,
    portfolioRemovalDecision,
} = {}) {
    const normalizedRevisionId = revisionId == null ? '' : String(revisionId).trim();
    if (!normalizedRevisionId) {
        return { success: false, message: 'Missing watchlist revision id.' };
    }
    try {
        return await callBackend(
            `api/watchlist/revisions/${encodeURIComponent(normalizedRevisionId)}/apply`,
            'POST',
            {
                attempt_id: attemptId,
                ...(expectedWatchlistRevisionId ? {
                    expected_watchlist_revision_id: expectedWatchlistRevisionId,
                } : {}),
                ...(portfolioRemovalDecision ? {
                    portfolio_removal_decision: portfolioRemovalDecision,
                } : {}),
            },
        );
    } catch (err) {
        return watchlistRevisionErrorResponse(err);
    }
}

export async function applyWatchlistContextRefreshRevision({
    revisionId,
    attemptId,
    expectedWatchlistRevisionId,
} = {}) {
    const normalizedRevisionId = revisionId == null ? '' : String(revisionId).trim();
    if (!normalizedRevisionId) {
        return { success: false, message: 'Missing watchlist revision id.' };
    }
    try {
        return await callBackend(
            `api/watchlist/revisions/${encodeURIComponent(normalizedRevisionId)}/apply-context-refresh-v1`,
            'POST',
            {
                attempt_id: attemptId,
                ...(expectedWatchlistRevisionId ? {
                    expected_watchlist_revision_id: expectedWatchlistRevisionId,
                } : {}),
            },
        );
    } catch (err) {
        return watchlistRevisionErrorResponse(err);
    }
}

export async function confirmWatchlistContextRemovals({
    revisionId,
    attemptId,
    reviewDigest,
} = {}) {
    const normalizedRevisionId = revisionId == null ? '' : String(revisionId).trim();
    if (!normalizedRevisionId) {
        return { success: false, message: 'Missing watchlist revision id.' };
    }
    try {
        return await callBackend(
            `api/watchlist/revisions/${encodeURIComponent(normalizedRevisionId)}/confirm-context-removals`,
            'POST',
            {
                attempt_id: attemptId,
                review_digest: reviewDigest,
            },
        );
    } catch (err) {
        return watchlistRevisionErrorResponse(err);
    }
}

export async function rejectWatchlistContextRemovals({
    revisionId,
    attemptId,
    reviewDigest,
} = {}) {
    const normalizedRevisionId = revisionId == null ? '' : String(revisionId).trim();
    if (!normalizedRevisionId) {
        return { success: false, message: 'Missing watchlist revision id.' };
    }
    try {
        return await callBackend(
            `api/watchlist/revisions/${encodeURIComponent(normalizedRevisionId)}/reject-context-removals`,
            'POST',
            {
                attempt_id: attemptId,
                review_digest: reviewDigest,
            },
        );
    } catch (err) {
        return watchlistRevisionErrorResponse(err);
    }
}

export async function keepWatchlistPortfolioSecurities({
    revisionId,
    attemptId,
    expectedWatchlistRevisionId,
} = {}) {
    const normalizedRevisionId = revisionId == null ? '' : String(revisionId).trim();
    if (!normalizedRevisionId) {
        return { success: false, message: 'Missing watchlist revision id.' };
    }
    try {
        return await callBackend(
            `api/watchlist/revisions/${encodeURIComponent(normalizedRevisionId)}/keep-portfolio-securities`,
            'POST',
            {
                attempt_id: attemptId,
                ...(expectedWatchlistRevisionId ? {
                    expected_watchlist_revision_id: expectedWatchlistRevisionId,
                } : {}),
            },
        );
    } catch (err) {
        return watchlistRevisionErrorResponse(err);
    }
}

export async function previewOptimizerWatchlistRestoration({
    portfolioId,
    expectedPortfolioVersion,
    expectedWatchlistRevisionId,
} = {}) {
    try {
        return await callBackend(
            'api/watchlist/revisions/optimizer-restoration/preview',
            'POST',
            {
                portfolio_id: portfolioId,
                expected_portfolio_version: expectedPortfolioVersion,
                expected_watchlist_revision_id: expectedWatchlistRevisionId,
            },
        );
    } catch (err) {
        return watchlistRevisionErrorResponse(err);
    }
}

export async function stageOptimizerWatchlistRestoration({
    portfolioId,
    expectedPortfolioVersion,
    expectedWatchlistRevisionId,
    attemptId,
} = {}) {
    try {
        return await callBackend(
            'api/watchlist/revisions/optimizer-restoration/stage',
            'POST',
            {
                portfolio_id: portfolioId,
                expected_portfolio_version: expectedPortfolioVersion,
                expected_watchlist_revision_id: expectedWatchlistRevisionId,
                attempt_id: attemptId,
            },
        );
    } catch (err) {
        return watchlistRevisionErrorResponse(err);
    }
}

export async function deleteOptimizerPortfolio(portfolioId) {
    return callBackend(
        'delete_what_if_portfolio',
        'POST',
        { portfolio_id: portfolioId },
    );
}

export async function fetchWatchlistUpdateOperationStatus({ revisionId } = {}) {
    const normalizedRevisionId = revisionId == null ? '' : String(revisionId).trim();
    if (!normalizedRevisionId) {
        throw new Error('Missing watchlist revision id.');
    }
    try {
        return await callBackend(
            `api/watchlist/revisions/${encodeURIComponent(normalizedRevisionId)}/operation-status`,
            'GET',
        );
    } catch (err) {
        throw watchlistRevisionThrownError(err);
    }
}

export async function cancelWatchlistUpdate({ revisionId, attemptId } = {}) {
    const normalizedRevisionId = revisionId == null ? '' : String(revisionId).trim();
    const normalizedAttemptId = attemptId == null ? '' : String(attemptId).trim();
    if (!normalizedRevisionId) {
        return { success: false, message: 'Missing watchlist revision id.' };
    }
    if (!normalizedAttemptId) {
        return { success: false, message: 'Missing cancellation attempt id.' };
    }
    try {
        return await callBackend(
            `api/watchlist/revisions/${encodeURIComponent(normalizedRevisionId)}/cancel`,
            'POST',
            { attempt_id: normalizedAttemptId },
        );
    } catch (err) {
        return watchlistRevisionErrorResponse(err);
    }
}

export async function recreateWatchlistRevision(revisionId) {
    try {
        return await callBackend(`api/watchlist/revisions/${encodeURIComponent(revisionId)}/recreate`, 'POST', {});
    } catch (err) {
        return watchlistRevisionErrorResponse(err);
    }
}

export async function ignoreWatchlistRevision({
    revisionId,
    attemptId,
    disposition,
} = {}) {
    const normalizedRevisionId = revisionId == null ? '' : String(revisionId).trim();
    if (!normalizedRevisionId) {
        return { success: false, message: 'Missing watchlist revision id.' };
    }
    try {
        return await callBackend(
            `api/watchlist/revisions/${encodeURIComponent(normalizedRevisionId)}/ignore`,
            'POST',
            {
                attempt_id: attemptId,
                ...(disposition ? { disposition } : {}),
            },
        );
    } catch (err) {
        return {
            success: false,
            message: failureMessage(err),
        };
    }
}

export async function stageWatchlistRestore({ revisionId } = {}) {
    const normalizedRevisionId = revisionId == null ? '' : String(revisionId).trim();
    if (!normalizedRevisionId) {
        return { success: false, message: 'Missing watchlist revision id.' };
    }
    try {
        return await callBackend(
            `api/watchlist/revisions/${encodeURIComponent(normalizedRevisionId)}/restore`,
            'POST',
            {},
        );
    } catch (err) {
        return watchlistRevisionErrorResponse(err);
    }
}

export async function stageWatchlistRestoreRows({ revisionId, rowIds, attemptId } = {}) {
    const normalizedRevisionId = revisionId == null ? '' : String(revisionId).trim();
    if (!normalizedRevisionId) {
        return { success: false, message: 'Missing watchlist revision id.' };
    }
    const normalizedRowIds = Array.isArray(rowIds)
        ? rowIds.map((id) => String(id)).filter((id) => id.trim())
        : [];
    if (!normalizedRowIds.length) {
        return { success: false, message: 'Select at least one security to add back.' };
    }
    try {
        return await callBackend(
            `api/watchlist/revisions/${encodeURIComponent(normalizedRevisionId)}/restore-rows`,
            'POST',
            { row_ids: normalizedRowIds, ...(attemptId ? { attempt_id: attemptId } : {}) },
        );
    } catch (err) {
        return watchlistRevisionErrorResponse(err);
    }
}

export async function discardWatchlistRevision({ revisionId } = {}) {
    const normalizedRevisionId = revisionId == null ? '' : String(revisionId).trim();
    if (!normalizedRevisionId) {
        return { success: false, message: 'Missing watchlist revision id.' };
    }
    try {
        return await callBackend(
            `api/watchlist/revisions/${encodeURIComponent(normalizedRevisionId)}/discard`,
            'POST',
            {},
        );
    } catch (err) {
        return watchlistRevisionErrorResponse(err);
    }
}

export async function saveMsiOutboundBarraChoice({
    sourceName,
    cogniveId,
    selectedBarrid,
    selectedCountry,
    selectedFingerprint,
} = {}) {
    try {
        return await callBackend('users/msi-outbound-barra-choice', 'POST', {
            source_name: sourceName,
            cognive_id: cogniveId,
            selected_barrid: selectedBarrid,
            selected_country: selectedCountry,
            selected_fingerprint: selectedFingerprint,
        });
    } catch (err) {
        return {
            success: false,
            message: failureMessage(err),
        };
    }
}

export async function markMsiDriftReviewed() {
    try {
        return await callBackend('users/msi-drift-mark-reviewed', 'POST');
    } catch (err) {
        return {
            success: false,
            message: failureMessage(err),
        };
    }
}
